import { API_BASE_URL } from "../config";

/**
 * Graph proxy service
 * Teaches: This tiny client wraps our backend Graph OBO proxy endpoints.
 * We POST a sharing link (or drive/item IDs) and receive the PDF bytes.
 * For task pane rendering, we return an ArrayBuffer so react-pdf can display it.
 */

export interface GraphProxyRequest {
  file_url?: string;
  drive_id?: string;
  item_id?: string;
}

/**
 * Calls the buffered Graph proxy to retrieve a PDF as ArrayBuffer.
 * - We use the "raw" variant for maximum viewer compatibility (returns Content-Length).
 * - Authentication: pass the user's access token to the backend; the backend performs OBO.
 */
export async function fetchGraphPdfAsArrayBuffer(
  payload: GraphProxyRequest,
  accessToken: string
): Promise<ArrayBuffer> {
  // We POST JSON; backend returns application/pdf bytes.
  const resp = await fetch(`${API_BASE_URL}/api/files/graph/stream-raw`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!resp.ok) {
    // Teach: If status is 401, token likely missing/expired. Other statuses bubble up for visibility.
    const text = await resp.text().catch(() => "");
    throw new Error(`Graph proxy failed: ${resp.status} ${resp.statusText} ${text}`);
  }

  // Teach: ArrayBuffer is ideal for react-pdf's { data } prop.
  return await resp.arrayBuffer();
}
